/**
 * pacsCompareUI.js — две кнопки режима сравнения: «Прошлые с PACS» и
 * «В архив». Тонкая обвязка: запросы шлёт существующий клиент
 * (io/dicomWebClient.js), разбор ответов — чистый io/pacsExchange.js
 * (проверен tests/pacs_exchange_test.mjs), загрузка серии идёт ШТАТНЫМ
 * конвейером файлов (retrieveSeriesAsFiles → handleFiles) — тем же, каким
 * открывается папка с диска, чтобы прошлые исследования появились везде,
 * где появляются обычные: в списке серий, в окнах сравнения, в индексе.
 *
 * ЧЕСТНОСТЬ:
 *   · без подключённого сервера кнопки не притворяются рабочими — говорят,
 *     где подключить (кнопка DICOMweb в верхнем ряду);
 *   · «В архив» шлёт ИСХОДНЫЕ файлы серии (те, что открывали с диска);
 *     серию, пришедшую с самого PACS или собранную программой, слать нечем —
 *     отказ с причиной, а не пустой POST;
 *   · ответ STOW разбирается на принято/отвергнуто: «200 OK» с частичным
 *     отказом внутри успехом не объявляется.
 */

import { getClient } from '../io/dicomWebClient.js';
import { разобратьПрошлые, разобратьСерии, сводкаSTOW, подписьПрошлого } from '../io/pacsExchange.js';
import { группироватьФайлыПоСериям } from '../io/studyArchiveModule.js';
import { getLastLoadedFiles, handleFiles } from './fileOps.js';
import { окно } from './quantTools.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const _кн = (текст, действие, главная = false) => {
  const б = document.createElement('button');
  б.textContent = текст;
  б.style.cssText = главная
    ? 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:6px 12px;cursor:pointer;'
    : 'background:#1d2939;border:1px solid #33415a;color:#e7eefc;border-radius:8px;padding:6px 12px;cursor:pointer;';
  б.addEventListener('click', действие);
  return б;
};

const _нетСервера = () => {
  showToast('Сервер PACS не подключён: кнопка «DICOMweb» в верхнем ряду — подключите и повторите', 'warning', 7000);
};

/**
 * [12.0.5] Кнопки серий исследования с загрузкой — ОДИН блок на два окна
 * («Прошлые с PACS» и «Рабочий список»): правило загрузки обязано быть одно.
 * Наполняет переданный контейнер; возвращает число серий.
 */
export async function кнопкиСерийИсследования(client, ст, контейнер, { послеЗагрузки } = {}) {
  const серии = разобратьСерии(await client.searchSeries(ст.studyUID));
  if (!серии.length) {
    контейнер.innerHTML = '<span style="color:#9fb3cf;">серий не нашлось</span>';
    return 0;
  }
  for (const с of серии) {
    const скн = _кн(`Загрузить: ${с.модальность || '?'} · ${с.кадров || '?'} кадр.${с.описание ? ' · ' + с.описание : ''}`,
      async () => {
        скн.disabled = true;
        const прежний = скн.textContent;
        скн.textContent = 'загружаю 0%';
        try {
          const файлы = await client.retrieveSeriesAsFiles(ст.studyUID, с.seriesUID,
            (n, t) => { скн.textContent = `загружаю ${Math.round((n / t) * 100)}%`; });
          if (!файлы.length) throw new Error('сервер вернул пустую серию');
          await handleFiles(файлы);
          скн.textContent = 'загружено ✓';
          showToast(`Исследование загружено (${файлы.length} кадров) — оно появилось в списке серий и в выборе окон`, 'success', 8000);
          try { послеЗагрузки?.(); } catch (e) { log.debug('[PACS] обновление окон:', e.message); }
        } catch (e) {
          скн.textContent = прежний; скн.disabled = false;
          showToast('Загрузка с PACS не вышла: ' + e.message, 'error', 8000);
        }
      }, true);
    скн.style.display = 'block'; скн.style.width = '100%'; скн.style.marginTop = '4px';
    контейнер.appendChild(скн);
  }
  return серии.length;
}

/**
 * «Прошлые с PACS»: QIDO по PatientID текущего пациента → список исследований
 * → серии выбранного → загрузка штатным конвейером.
 * @param {object} ктх — { patientId, patientName, загруженныеUID: Set,
 *                         послеЗагрузки: () => void }
 */
export async function открытьПрошлыеPACS(ктх = {}) {
  const client = getClient();
  if (!client) { _нетСервера(); return; }
  if (!ктх.patientId) {
    showToast('У открытого исследования нет PatientID (0010,0020) — искать прошлые не по чему', 'warning', 7000);
    return;
  }
  const w = окно(`Прошлые с PACS — ${ктх.patientName || ктх.patientId}`, { ширина: 640 });
  const тело = w.тело;
  тело.innerHTML = '<div style="color:#9fb3cf;">Спрашиваю сервер (QIDO по PatientID)…</div>';

  let список;
  try {
    const ответ = await client.searchStudies({ PatientID: ктх.patientId });
    список = разобратьПрошлые(ответ, { patientId: ктх.patientId, исключитьUID: ктх.загруженныеUID || new Set() });
  } catch (e) {
    тело.innerHTML = `<div style="color:#f2b8b5;">Сервер не ответил: ${e.message}</div>`;
    return;
  }
  if (список.отсеяноЧужих) {
    log.warn(`[PACS] сервер вернул ${список.отсеяноЧужих} исследований ЧУЖОГО пациента на запрос PatientID=${ктх.patientId} — отсеяны`);
  }
  if (!список.исследования.length) {
    тело.innerHTML = '<div style="color:#9fb3cf;">Прошлых исследований этого пациента на сервере не нашлось'
      + (ктх.загруженныеUID?.size ? ' (уже загруженные скрыты)' : '') + '.</div>';
    return;
  }

  тело.innerHTML = '';
  for (const ст of список.исследования) {
    const строка = document.createElement('div');
    строка.style.cssText = 'display:flex;align-items:center;gap:8px;justify-content:space-between;'
      + 'border:1px solid #223047;border-radius:8px;padding:8px 10px;margin:6px 0;';
    const подпись = document.createElement('div');
    подпись.textContent = подписьПрошлого(ст);
    const кн = _кн('Серии…', async () => {
      кн.disabled = true; кн.textContent = 'спрашиваю…';
      try {
        const блок = document.createElement('div');
        блок.style.cssText = 'width:100%;margin-top:6px;';
        await кнопкиСерийИсследования(client, ст, блок, { послеЗагрузки: ктх.послеЗагрузки });
        кн.remove();
        строка.appendChild(блок);
        строка.style.flexWrap = 'wrap';
      } catch (e) {
        кн.textContent = 'Серии…'; кн.disabled = false;
        showToast('Список серий не пришёл: ' + e.message, 'error', 7000);
      }
    });
    строка.append(подпись, кн);
    тело.appendChild(строка);
  }
  тело.insertAdjacentHTML('beforeend',
    '<div style="margin-top:8px;color:#9fb3cf;">Загрузка идёт тем же путём, что открытие папки: серия появится в списке слева и в выборе окон сравнения.</div>');
}

/**
 * «В архив»: отправить исходные файлы выбранной серии на сервер (STOW-RS).
 * @param {object} ктх — { окна: [{подпись, seriesUID, studyUID}] }
 */
export async function отправитьВАрхив(ктх = {}) {
  const client = getClient();
  if (!client) { _нетСервера(); return; }
  const кандидаты = (ктх.окна || []).filter((о) => о.seriesUID);
  if (!кандидаты.length) { showToast('В окнах сравнения нет серий — отправлять нечего', 'warning', 5000); return; }

  const w = окно('В архив (STOW-RS)', { ширина: 560 });
  w.тело.innerHTML = '<div style="color:#9fb3cf;margin-bottom:6px;">Что отправить: шлём ИСХОДНЫЕ файлы серии — те, что открывались с диска.</div>';
  const итог = document.createElement('div');

  for (const о of кандидаты) {
    const кн = _кн(`Отправить: ${о.подпись}`, async () => {
      кн.disabled = true;
      const прежний = кн.textContent;
      кн.textContent = 'собираю файлы…';
      try {
        const все = getLastLoadedFiles?.() || [];
        if (!все.length) throw new Error('в памяти нет исходных файлов (серии пришли не с диска этой сессии)');
        const поСериям = await группироватьФайлыПоСериям(все, () => {});
        const файлы = поСериям.get(о.seriesUID) || [];
        if (!файлы.length) throw new Error('исходные файлы ИМЕННО этой серии в памяти не нашлись — вероятно, серия пришла с PACS или собрана программой');
        кн.textContent = `шлю ${файлы.length} файлов…`;
        const ответ = await client.storeInstances(файлы, о.studyUID || null);
        const с = сводкаSTOW(ответ);
        кн.textContent = прежний; кн.disabled = false;
        итог.innerHTML = с.всёПринято
          ? `<div style="color:#7ee2a8;margin-top:6px;">Сервер принял все ${с.принято} файлов.</div>`
          : `<div style="color:#f2b8b5;margin-top:6px;">Принято ${с.принято}, ОТВЕРГНУТО ${с.отвергнуто}`
            + (с.причины.length ? ` (${с.причины.join('; ')})` : '')
            + ' — отвергнутые В АРХИВ НЕ ПОПАЛИ.</div>';
        showToast(с.всёПринято ? `В архив принято ${с.принято} файлов` : `Архив принял ${с.принято}, отверг ${с.отвергнуто}`,
          с.всёПринято ? 'success' : 'warning', 8000);
      } catch (e) {
        кн.textContent = прежний; кн.disabled = false;
        итог.innerHTML = `<div style="color:#f2b8b5;margin-top:6px;">Не отправилось: ${e.message}</div>`;
      }
    }, true);
    кн.style.display = 'block'; кн.style.width = '100%'; кн.style.marginTop = '6px';
    w.тело.appendChild(кн);
  }
  w.тело.appendChild(итог);
}
